/**
 * <DestinationEditor /> — admin editor for the full travel guide of one destination.
 * 8 tabs: General · Things to Do · Photo Spots · Restaurants · Gulf · Itinerary · Stay · Getting There
 *
 * Route: /admin/destinations/:slug/edit
 * Reads /api/destinations + writes /api/admin/destinations/:id (existing endpoint, accepts arbitrary subset).
 */
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Save, Plus, Trash2, Image as ImageIcon, ListTodo, Camera, UtensilsCrossed,
    Moon, MapPin, Bed, Car, ExternalLink,
} from "lucide-react";
import { api_get, api_put, apiError } from "../../lib/api";
import ImageUploader from "../../components/ImageUploader";
import SmartImage from "../../components/SmartImage";

const ICON_OPTIONS = [
    { v: "nature", l: "🏔️ Nature" },
    { v: "mountain", l: "⛰️ Mountain" },
    { v: "culture", l: "🕌 Culture" },
    { v: "adventure", l: "🚤 Adventure" },
    { v: "experience", l: "🍵 Experience" },
    { v: "wildlife", l: "🐘 Wildlife" },
    { v: "beach", l: "🏖️ Beach" },
    { v: "viewpoint", l: "📸 Viewpoint" },
];
const TIME_ICON_OPTIONS = [
    { v: "sunrise", l: "🌅 Sunrise" },
    { v: "golden", l: "🌇 Golden hour" },
    { v: "anytime", l: "🕒 Anytime" },
];
const RESTAURANT_TAGS = [
    "Halal", "Vegetarian", "Family", "Local favourite", "Rooftop", "Scenic", "Seafood",
    "Waterfront", "Fine dining", "Budget", "Breakfast", "Clean", "Local", "Café", "Art",
];
const PRICE_RANGES = [
    { v: "₹", l: "₹ (Budget)" }, { v: "₹₹", l: "₹₹ (Mid-range)" }, { v: "₹₹₹", l: "₹₹₹ (Premium)" },
];

/* ---------- generic helpers ---------- */
const Field = ({ label, children, hint }) => (
    <label className="block">
        <span className="text-xs uppercase tracking-widest text-ink-light font-semibold">{label}</span>
        {children}
        {hint && <span className="text-[11px] text-ink-light/80 mt-0.5 block">{hint}</span>}
    </label>
);
const Input = (props) => (
    <input
        {...props}
        className={`mt-1 w-full px-3 py-2 rounded-lg border border-cream-200 bg-white focus:border-ink outline-none text-sm ${props.className || ""}`}
    />
);
const TextArea = (props) => (
    <textarea
        rows={3}
        {...props}
        className={`mt-1 w-full px-3 py-2 rounded-lg border border-cream-200 bg-white focus:border-ink outline-none text-sm leading-relaxed ${props.className || ""}`}
    />
);
const Select = ({ options, ...props }) => (
    <select
        {...props}
        className={`mt-1 w-full px-3 py-2 rounded-lg border border-cream-200 bg-white focus:border-ink outline-none text-sm ${props.className || ""}`}
    >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
);

/* ============ TAB 1 — General / Hero ============ */
const TabGeneral = ({ d, setD }) => {
    const [bestForRaw, setBestForRaw] = useState((d.bestFor || []).join(", "));
    useEffect(() => {
        setD((cur) => ({ ...cur, bestFor: bestForRaw.split(",").map((s) => s.trim()).filter(Boolean) }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bestForRaw]);

    const qf = d.quickFacts || {};
    const setQf = (k, v) => setD({ ...d, quickFacts: { ...qf, [k]: v } });

    return (
        <div className="space-y-6">
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                    <Field label="Hero background image">
                        <ImageUploader value={d.imageUrl} onUploaded={(url) => setD({ ...d, imageUrl: url })} />
                    </Field>
                </div>
                <Field label="Page title (city)"><Input value={d.title || ""} onChange={(e) => setD({ ...d, title: e.target.value })} /></Field>
                <Field label="Hero subtitle"><Input value={d.subtitle || ""} onChange={(e) => setD({ ...d, subtitle: e.target.value })} /></Field>
                <Field label="About title"><Input value={d.aboutTitle || ""} onChange={(e) => setD({ ...d, aboutTitle: e.target.value })} placeholder={`About ${d.title || "this city"}`} /></Field>
                <Field label="Category"><Input value={d.category || ""} onChange={(e) => setD({ ...d, category: e.target.value })} /></Field>
                <div className="md:col-span-2">
                    <Field label="Long description">
                        <TextArea rows={5} value={d.longDescription || ""} onChange={(e) => setD({ ...d, longDescription: e.target.value })} />
                    </Field>
                </div>
                <div className="md:col-span-2">
                    <Field label="Best For tags (comma-separated)">
                        <Input value={bestForRaw} onChange={(e) => setBestForRaw(e.target.value)} placeholder="Couples, Families, Photographers" />
                    </Field>
                </div>
            </section>

            <section>
                <h3 className="text-sm font-bold text-ink mb-2">CTA Buttons</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Button 1 text"><Input value={d.buttonText || ""} onChange={(e) => setD({ ...d, buttonText: e.target.value })} /></Field>
                    <Field label="Button 1 URL"><Input value={d.buttonUrl || ""} onChange={(e) => setD({ ...d, buttonUrl: e.target.value })} /></Field>
                    <Field label="Button 2 text"><Input value={d.button2Text || ""} onChange={(e) => setD({ ...d, button2Text: e.target.value })} /></Field>
                    <Field label="Button 2 URL"><Input value={d.button2Url || ""} onChange={(e) => setD({ ...d, button2Url: e.target.value })} /></Field>
                </div>
            </section>

            <section>
                <h3 className="text-sm font-bold text-ink mb-2">Quick Facts</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Distance / Drive"><Input value={d.distanceDuration || ""} onChange={(e) => setD({ ...d, distanceDuration: e.target.value })} /></Field>
                    <Field label="Best Season"><Input value={d.bestSeason || ""} onChange={(e) => setD({ ...d, bestSeason: e.target.value })} /></Field>
                    <Field label="Altitude"><Input value={qf.altitude || ""} onChange={(e) => setQf("altitude", e.target.value)} /></Field>
                    <Field label="Entry Fees"><Input value={qf.entryFees || ""} onChange={(e) => setQf("entryFees", e.target.value)} /></Field>
                    <Field label="Connectivity"><Input value={qf.connectivity || ""} onChange={(e) => setQf("connectivity", e.target.value)} /></Field>
                    <Field label="Halal Friendly"><Input value={qf.halalFriendly || ""} onChange={(e) => setQf("halalFriendly", e.target.value)} /></Field>
                    <Field label="Known As (optional)"><Input value={qf.knownAs || ""} onChange={(e) => setQf("knownAs", e.target.value)} /></Field>
                    <Field label="Famous For (optional)"><Input value={qf.famousFor || ""} onChange={(e) => setQf("famousFor", e.target.value)} /></Field>
                </div>
            </section>
        </div>
    );
};

/* ============ TAB 2 — Things to Do ============ */
const TabThingsToDo = ({ d, setD }) => {
    const items = d.thingsToDo || [];
    const titles = d.sectionTitles || {};
    const update = (i, patch) => {
        const next = items.map((it, ii) => (ii === i ? { ...it, ...patch } : it));
        setD({ ...d, thingsToDo: next });
    };
    const remove = (i) => setD({ ...d, thingsToDo: items.filter((_, ii) => ii !== i) });
    const add = () => setD({ ...d, thingsToDo: [...items, { icon: "experience", name: "", desc: "", hours: "", price: "", tip: "", displayOrder: (items.length + 1) * 10 }] });
    return (
        <div className="space-y-4">
            <Field label="Section title">
                <Input value={titles.thingsToDo || ""} onChange={(e) => setD({ ...d, sectionTitles: { ...titles, thingsToDo: e.target.value } })} placeholder="What to do in …" />
            </Field>
            {items.map((it, i) => (
                <div key={i} className="border border-cream-200 rounded-xl p-4 bg-cream-50/50" data-testid={`ttd-${i}`}>
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold text-ink-light">#{i + 1}</span>
                        <button type="button" onClick={() => remove(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`ttd-${i}-del`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Icon"><Select options={ICON_OPTIONS} value={it.icon || "experience"} onChange={(e) => update(i, { icon: e.target.value })} /></Field>
                        <Field label="Name"><Input value={it.name || ""} onChange={(e) => update(i, { name: e.target.value })} /></Field>
                        <div className="md:col-span-2"><Field label="Short description"><TextArea value={it.desc || ""} onChange={(e) => update(i, { desc: e.target.value })} /></Field></div>
                        <Field label="Hours"><Input value={it.hours || ""} onChange={(e) => update(i, { hours: e.target.value })} placeholder="7am–4pm" /></Field>
                        <Field label="Entry price"><Input value={it.price || ""} onChange={(e) => update(i, { price: e.target.value })} placeholder="₹125/adult or Free" /></Field>
                        <div className="md:col-span-2"><Field label="Tip (yellow highlight)"><Input value={it.tip || ""} onChange={(e) => update(i, { tip: e.target.value })} /></Field></div>
                        <Field label="Display order"><Input type="number" value={it.displayOrder ?? 100} onChange={(e) => update(i, { displayOrder: Number(e.target.value) })} /></Field>
                    </div>
                </div>
            ))}
            <button type="button" onClick={add} className="w-full py-3 border-2 border-dashed border-cream-300 rounded-xl text-ink-light hover:bg-cream-50 flex items-center justify-center gap-2 text-sm" data-testid="ttd-add"><Plus className="w-4 h-4" /> Add activity</button>
        </div>
    );
};

/* ============ TAB 3 — Photo Spots ============ */
const TabPhotoSpots = ({ d, setD }) => {
    const items = d.photoSpots || [];
    const titles = d.sectionTitles || {};
    const update = (i, patch) => setD({ ...d, photoSpots: items.map((it, ii) => (ii === i ? { ...it, ...patch } : it)) });
    const remove = (i) => setD({ ...d, photoSpots: items.filter((_, ii) => ii !== i) });
    const add = () => setD({ ...d, photoSpots: [...items, { image: "", name: "", bestTime: "", timeIcon: "anytime", tip: "", displayOrder: (items.length + 1) * 10 }] });
    return (
        <div className="space-y-4">
            <Field label="Section title">
                <Input value={titles.photoSpots || ""} onChange={(e) => setD({ ...d, sectionTitles: { ...titles, photoSpots: e.target.value } })} placeholder="Best photo spots" />
            </Field>
            {items.map((it, i) => (
                <div key={i} className="border border-cream-200 rounded-xl p-4 bg-cream-50/50" data-testid={`spot-${i}`}>
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold text-ink-light">#{i + 1}</span>
                        <button type="button" onClick={() => remove(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <Field label="Image"><ImageUploader value={it.image} onUploaded={(url) => update(i, { image: url })} /></Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                        <Field label="Name"><Input value={it.name || ""} onChange={(e) => update(i, { name: e.target.value })} /></Field>
                        <Field label="Best time"><Input value={it.bestTime || ""} onChange={(e) => update(i, { bestTime: e.target.value })} placeholder="Sunrise (6–7am)" /></Field>
                        <Field label="Time icon"><Select options={TIME_ICON_OPTIONS} value={it.timeIcon || "anytime"} onChange={(e) => update(i, { timeIcon: e.target.value })} /></Field>
                        <Field label="Display order"><Input type="number" value={it.displayOrder ?? 100} onChange={(e) => update(i, { displayOrder: Number(e.target.value) })} /></Field>
                        <div className="md:col-span-2"><Field label="Photography tip"><TextArea value={it.tip || ""} onChange={(e) => update(i, { tip: e.target.value })} /></Field></div>
                    </div>
                </div>
            ))}
            <button type="button" onClick={add} className="w-full py-3 border-2 border-dashed border-cream-300 rounded-xl text-ink-light hover:bg-cream-50 flex items-center justify-center gap-2 text-sm"><Plus className="w-4 h-4" /> Add photo spot</button>
        </div>
    );
};

/* ============ TAB 4 — Restaurants ============ */
const TabRestaurants = ({ d, setD }) => {
    const items = d.restaurants || [];
    const titles = d.sectionTitles || {};
    const update = (i, patch) => setD({ ...d, restaurants: items.map((it, ii) => (ii === i ? { ...it, ...patch } : it)) });
    const remove = (i) => setD({ ...d, restaurants: items.filter((_, ii) => ii !== i) });
    const add = () => setD({ ...d, restaurants: [...items, { name: "", cuisine: "", price: "₹₹", halal: false, desc: "", tags: [] }] });
    const toggleTag = (i, tag) => {
        const tags = items[i].tags || [];
        update(i, { tags: tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag] });
    };
    return (
        <div className="space-y-4">
            <Field label="Section title">
                <Input value={titles.restaurants || ""} onChange={(e) => setD({ ...d, sectionTitles: { ...titles, restaurants: e.target.value } })} placeholder="Where to eat" />
            </Field>
            {items.map((it, i) => (
                <div key={i} className="border border-cream-200 rounded-xl p-4 bg-cream-50/50" data-testid={`rest-${i}`}>
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold text-ink-light">#{i + 1}</span>
                        <button type="button" onClick={() => remove(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Name"><Input value={it.name || ""} onChange={(e) => update(i, { name: e.target.value })} /></Field>
                        <Field label="Cuisine"><Input value={it.cuisine || ""} onChange={(e) => update(i, { cuisine: e.target.value })} placeholder="Kerala & North Indian" /></Field>
                        <Field label="Price range"><Select options={PRICE_RANGES} value={it.price || "₹₹"} onChange={(e) => update(i, { price: e.target.value })} /></Field>
                        <label className="flex items-center gap-2 mt-7 text-sm font-semibold text-ink">
                            <input type="checkbox" checked={it.halal || false} onChange={(e) => update(i, { halal: e.target.checked })} className="w-4 h-4 accent-emerald-600" />
                            Halal certified (shows green badge)
                        </label>
                        <div className="md:col-span-2"><Field label="Description"><TextArea value={it.desc || ""} onChange={(e) => update(i, { desc: e.target.value })} /></Field></div>
                        <div className="md:col-span-2">
                            <span className="text-xs uppercase tracking-widest text-ink-light font-semibold">Tags</span>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {RESTAURANT_TAGS.map((tag) => (
                                    <button key={tag} type="button" onClick={() => toggleTag(i, tag)}
                                        className={`px-2.5 py-1 rounded-full text-xs font-medium border ${(it.tags || []).includes(tag) ? "bg-ink text-white border-ink" : "bg-white text-ink-light border-cream-200"}`}>
                                        {tag}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            <button type="button" onClick={add} className="w-full py-3 border-2 border-dashed border-cream-300 rounded-xl text-ink-light hover:bg-cream-50 flex items-center justify-center gap-2 text-sm"><Plus className="w-4 h-4" /> Add restaurant</button>
        </div>
    );
};

/* ============ TAB 5 — Gulf travellers ============ */
const TabGulf = ({ d, setD }) => {
    const g = d.gulfTravellers || {};
    const titles = d.sectionTitles || {};
    const set = (k, v) => setD({ ...d, gulfTravellers: { ...g, [k]: v } });
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
                <Field label="Section title">
                    <Input value={titles.gulf || ""} onChange={(e) => setD({ ...d, sectionTitles: { ...titles, gulf: e.target.value } })} placeholder="For Gulf & Muslim travellers" />
                </Field>
            </div>
            <Field label="Badge label"><Input value={g.badgeLabel || ""} onChange={(e) => set("badgeLabel", e.target.value)} placeholder="Halal • Prayer-friendly" /></Field>
            <Field label="Arabic title"><Input dir="rtl" value={g.arabicTitle || ""} onChange={(e) => set("arabicTitle", e.target.value)} /></Field>
            <Field label="Nearest Mosque"><Input value={g.mosque || ""} onChange={(e) => set("mosque", e.target.value)} /></Field>
            <Field label="Halal Restaurants"><Input value={g.halalRestaurants || ""} onChange={(e) => set("halalRestaurants", e.target.value)} placeholder="Restaurant 1 · Restaurant 2 · Restaurant 3" /></Field>
            <Field label="Prayer-Friendly Hotels"><Input value={g.prayerFriendlyHotels || ""} onChange={(e) => set("prayerFriendlyHotels", e.target.value)} /></Field>
            <Field label="Dry Hotels (no alcohol)"><Input value={g.dryHotels || ""} onChange={(e) => set("dryHotels", e.target.value)} /></Field>
            <div className="md:col-span-2"><Field label="Qibla / Mosque info"><TextArea value={g.qibla || ""} onChange={(e) => set("qibla", e.target.value)} /></Field></div>
            <div className="md:col-span-2"><Field label="Arabic description (RTL)"><TextArea dir="rtl" value={g.arabicNote || ""} onChange={(e) => set("arabicNote", e.target.value)} /></Field></div>
            <div className="md:col-span-2"><Field label="Footer note"><Input value={g.footerNote || ""} onChange={(e) => set("footerNote", e.target.value)} placeholder="Your SouthTrip driver knows every mosque…" /></Field></div>
        </div>
    );
};

/* ============ TAB 6 — Itinerary ============ */
const TabItinerary = ({ d, setD }) => {
    const itin = d.itineraries || {};
    const titles = d.sectionTitles || {};
    const cta = d.itineraryCta || {};
    const [activeDay, setActiveDay] = useState("1 Day");

    /* Normalize: convert a day value (object OR array of day blocks) into editable rows */
    const dayValue = itin[activeDay];
    const isMultiDay = Array.isArray(dayValue);
    const blocks = isMultiDay ? dayValue : [{ day: "", ...(dayValue || {}) }];

    const setDayBlocks = (newBlocks) => {
        const value = (activeDay === "1 Day") ? { Morning: newBlocks[0]?.Morning || "", Afternoon: newBlocks[0]?.Afternoon || "", Evening: newBlocks[0]?.Evening || "" } : newBlocks;
        setD({ ...d, itineraries: { ...itin, [activeDay]: value } });
    };
    const updateBlock = (idx, patch) => setDayBlocks(blocks.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
    const addBlock = () => setDayBlocks([...blocks, { day: `Day ${blocks.length + 1}`, Morning: "", Afternoon: "", Evening: "" }]);
    const removeBlock = (idx) => setDayBlocks(blocks.filter((_, i) => i !== idx));

    return (
        <div className="space-y-5">
            <Field label="Section title">
                <Input value={titles.itinerary || ""} onChange={(e) => setD({ ...d, sectionTitles: { ...titles, itinerary: e.target.value } })} placeholder="Suggested itinerary" />
            </Field>
            <div className="flex gap-2">
                {["1 Day", "2 Days", "3 Days"].map((tab) => (
                    <button key={tab} type="button" onClick={() => setActiveDay(tab)}
                        className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${activeDay === tab ? "bg-ink text-white border-ink" : "bg-white text-ink-light border-cream-200"}`}>
                        {tab}
                    </button>
                ))}
            </div>

            {blocks.map((b, i) => (
                <div key={i} className="border border-cream-200 rounded-xl p-4 bg-cream-50/50">
                    {isMultiDay && (
                        <div className="flex items-center justify-between mb-3">
                            <Input value={b.day || ""} onChange={(e) => updateBlock(i, { day: e.target.value })} className="max-w-[180px] font-semibold" placeholder={`Day ${i + 1}`} />
                            <button type="button" onClick={() => removeBlock(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                    )}
                    <Field label="Morning"><TextArea rows={2} value={b.Morning || ""} onChange={(e) => updateBlock(i, { Morning: e.target.value })} /></Field>
                    <div className="mt-3"><Field label="Afternoon"><TextArea rows={2} value={b.Afternoon || ""} onChange={(e) => updateBlock(i, { Afternoon: e.target.value })} /></Field></div>
                    <div className="mt-3"><Field label="Evening"><TextArea rows={2} value={b.Evening || ""} onChange={(e) => updateBlock(i, { Evening: e.target.value })} /></Field></div>
                </div>
            ))}
            {activeDay !== "1 Day" && (
                <button type="button" onClick={addBlock} className="w-full py-2 border-2 border-dashed border-cream-300 rounded-xl text-ink-light hover:bg-cream-50 flex items-center justify-center gap-2 text-sm"><Plus className="w-4 h-4" /> Add day</button>
            )}

            <div className="border-t border-cream-200 pt-4">
                <h4 className="text-xs uppercase tracking-widest font-bold text-ink-light mb-3">CTA Button</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Text"><Input value={cta.text || ""} onChange={(e) => setD({ ...d, itineraryCta: { ...cta, text: e.target.value } })} placeholder="Book a cab for this itinerary" /></Field>
                    <Field label="URL"><Input value={cta.url || ""} onChange={(e) => setD({ ...d, itineraryCta: { ...cta, url: e.target.value } })} placeholder="/cab-booking" /></Field>
                </div>
            </div>
        </div>
    );
};

/* ============ TAB 7 — Where to Stay ============ */
const TabStay = ({ d, setD }) => {
    const h = d.hotels || {};
    const titles = d.sectionTitles || {};
    const cols = [["budget", "Budget"], ["midRange", "Mid-range"], ["luxury", "Luxury"]];
    const update = (col, idx, patch) => {
        const arr = (h[col] || []).map((x, i) => (i === idx ? { ...x, ...patch } : x));
        setD({ ...d, hotels: { ...h, [col]: arr } });
    };
    const remove = (col, idx) => setD({ ...d, hotels: { ...h, [col]: (h[col] || []).filter((_, i) => i !== idx) } });
    const add = (col) => setD({ ...d, hotels: { ...h, [col]: [...(h[col] || []), { name: "", price: "", highlight: "" }] } });
    return (
        <div className="space-y-5">
            <Field label="Section title">
                <Input value={titles.stay || ""} onChange={(e) => setD({ ...d, sectionTitles: { ...titles, stay: e.target.value } })} placeholder="Where to stay" />
            </Field>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {cols.map(([k, label]) => (
                    <div key={k} className="border border-cream-200 rounded-xl p-4 bg-cream-50/30">
                        <h4 className="font-semibold text-ink text-sm mb-3 flex items-center gap-1.5"><Bed className="w-4 h-4 text-ink-light" /> {label}</h4>
                        {(h[k] || []).map((it, i) => (
                            <div key={i} className="bg-white border border-cream-200 rounded-lg p-3 mb-3">
                                <div className="flex items-center justify-end mb-1">
                                    <button type="button" onClick={() => remove(k, i)} className="p-1 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                                <Field label="Name"><Input value={it.name || ""} onChange={(e) => update(k, i, { name: e.target.value })} /></Field>
                                <div className="mt-2"><Field label="Price /night" hint="Range without ₹ symbol, e.g. 3,500–7,000"><Input value={it.price || ""} onChange={(e) => update(k, i, { price: e.target.value })} /></Field></div>
                                <div className="mt-2"><Field label="Highlight"><Input value={it.highlight || ""} onChange={(e) => update(k, i, { highlight: e.target.value })} /></Field></div>
                            </div>
                        ))}
                        <button type="button" onClick={() => add(k)} className="w-full py-2 border-2 border-dashed border-cream-300 rounded-lg text-ink-light hover:bg-white flex items-center justify-center gap-1.5 text-xs"><Plus className="w-3.5 h-3.5" /> Add hotel</button>
                    </div>
                ))}
            </div>
            <Field label="Footer note"><Input value={d.hotelsFooterNote || ""} onChange={(e) => setD({ ...d, hotelsFooterNote: e.target.value })} /></Field>
        </div>
    );
};

/* ============ TAB 8 — Getting There ============ */
const TabGettingThere = ({ d, setD }) => {
    const titles = d.sectionTitles || {};
    const prices = d.prices || [];
    const ctas = d.gettingThereCtas || [];
    const updateRow = (i, patch) => setD({ ...d, prices: prices.map((p, ii) => (ii === i ? { ...p, ...patch } : p)) });
    const removeRow = (i) => setD({ ...d, prices: prices.filter((_, ii) => ii !== i) });
    const addRow = () => setD({ ...d, prices: [...prices, { from: "", sedan: 0, suv: 0 }] });

    const updateCta = (i, patch) => setD({ ...d, gettingThereCtas: ctas.map((c, ii) => (ii === i ? { ...c, ...patch } : c)) });
    return (
        <div className="space-y-5">
            <Field label="Section title">
                <Input value={titles.gettingThere || ""} onChange={(e) => setD({ ...d, sectionTitles: { ...titles, gettingThere: e.target.value } })} placeholder="Getting to … with SouthTrip" />
            </Field>
            <div>
                <h4 className="text-xs uppercase tracking-widest font-bold text-ink-light mb-2">Price table</h4>
                <div className="border border-cream-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-cream-100">
                            <tr><th className="text-left p-2 font-semibold">From</th><th className="text-right p-2 font-semibold">Sedan / day (₹)</th><th className="text-right p-2 font-semibold">SUV / day (₹)</th><th className="p-2"></th></tr>
                        </thead>
                        <tbody>
                            {prices.map((p, i) => (
                                <tr key={i} className="border-t border-cream-200">
                                    <td className="p-2"><input value={p.from || ""} onChange={(e) => updateRow(i, { from: e.target.value })} className="w-full px-2 py-1.5 rounded border border-cream-200" /></td>
                                    <td className="p-2"><input type="number" value={p.sedan ?? 0} onChange={(e) => updateRow(i, { sedan: Number(e.target.value) })} className="w-32 px-2 py-1.5 rounded border border-cream-200 text-right" /></td>
                                    <td className="p-2"><input type="number" value={p.suv ?? 0} onChange={(e) => updateRow(i, { suv: Number(e.target.value) })} className="w-32 px-2 py-1.5 rounded border border-cream-200 text-right" /></td>
                                    <td className="p-2 text-right"><button type="button" onClick={() => removeRow(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <button type="button" onClick={addRow} className="mt-2 w-full py-2 border-2 border-dashed border-cream-300 rounded-xl text-ink-light hover:bg-cream-50 flex items-center justify-center gap-1.5 text-sm"><Plus className="w-4 h-4" /> Add price row</button>
            </div>
            <div>
                <h4 className="text-xs uppercase tracking-widest font-bold text-ink-light mb-2">CTA buttons</h4>
                <div className="space-y-3">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-cream-200 rounded-lg p-3">
                            <Field label={`Button ${i + 1} text`}><Input value={ctas[i]?.text || ""} onChange={(e) => { const next = [...ctas]; next[i] = { ...(next[i] || {}), text: e.target.value }; setD({ ...d, gettingThereCtas: next }); }} /></Field>
                            <Field label={`Button ${i + 1} URL`}><Input value={ctas[i]?.url || ""} onChange={(e) => { const next = [...ctas]; next[i] = { ...(next[i] || {}), url: e.target.value }; setD({ ...d, gettingThereCtas: next }); }} /></Field>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

/* ============ Main wrapper ============ */
const TABS = [
    { id: "general", label: "General & Hero", icon: ImageIcon, Comp: TabGeneral },
    { id: "thingsToDo", label: "Things to Do", icon: ListTodo, Comp: TabThingsToDo },
    { id: "photoSpots", label: "Photo Spots", icon: Camera, Comp: TabPhotoSpots },
    { id: "restaurants", label: "Restaurants", icon: UtensilsCrossed, Comp: TabRestaurants },
    { id: "gulf", label: "Gulf travellers", icon: Moon, Comp: TabGulf },
    { id: "itinerary", label: "Itinerary", icon: MapPin, Comp: TabItinerary },
    { id: "stay", label: "Where to Stay", icon: Bed, Comp: TabStay },
    { id: "gettingThere", label: "Getting There", icon: Car, Comp: TabGettingThere },
];

const DestinationEditor = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [d, setD] = useState(null);
    const [activeTab, setActiveTab] = useState("general");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api_get(`/destinations/${slug}`)
            .then(setD)
            .catch((e) => toast.error(apiError(e)));
    }, [slug]);

    const save = async () => {
        if (!d?.id) return;
        setSaving(true);
        try {
            // Strip readonly fields
            const { id, createdAt, updatedAt, ...payload } = d;
            await api_put(`/admin/destinations/${id}`, payload);
            toast.success("Saved");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (!d) return <div className="p-8 text-ink-light text-sm" data-testid="dest-editor-loading">Loading…</div>;

    const ActiveComp = TABS.find((t) => t.id === activeTab)?.Comp || TabGeneral;

    return (
        <div className="max-w-6xl mx-auto pb-32" data-testid="destination-editor">
            <header className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                    <Link to="/admin/destinations" className="p-2 rounded-lg hover:bg-cream-100 text-ink-light"><ArrowLeft className="w-4 h-4" /></Link>
                    <div>
                        <div className="text-xs uppercase tracking-widest text-ink-light">Destination editor</div>
                        <h1 className="font-display text-2xl text-ink font-bold flex items-center gap-2">
                            <SmartImage src={d.imageUrl} alt="" className="w-8 h-8 rounded-md" /> {d.title}
                        </h1>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <a href={`/destinations/${slug}`} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg border border-cream-200 hover:bg-cream-50 text-sm flex items-center gap-1.5 text-ink-light">
                        View public page <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-ink text-white font-semibold text-sm flex items-center gap-1.5 disabled:opacity-60" data-testid="dest-editor-save">
                        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </header>

            {/* Tabs */}
            <nav className="bg-white border border-cream-200 rounded-xl p-1.5 flex flex-wrap gap-1 mb-6 overflow-x-auto" data-testid="dest-editor-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = activeTab === t.id;
                    return (
                        <button key={t.id} type="button" onClick={() => setActiveTab(t.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-1.5 transition ${active ? "bg-ink text-white" : "text-ink-light hover:bg-cream-100"}`}
                            data-testid={`dest-editor-tab-${t.id}`}>
                            <Icon className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">{t.label}</span>
                        </button>
                    );
                })}
            </nav>

            {/* Active tab content */}
            <div className="bg-white border border-cream-200 rounded-2xl p-5 md:p-6">
                <ActiveComp d={d} setD={setD} />
            </div>

            {/* Sticky save bar */}
            <div className="fixed bottom-4 right-4 z-30">
                <button onClick={save} disabled={saving} className="px-5 py-3 rounded-full bg-ink text-white shadow-lg font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
                    <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
                </button>
            </div>
        </div>
    );
};

export default DestinationEditor;
